import Utils from "context/utils";
import { tokenSafeName } from "./attachments";

/**
 * content.js — the content ⇄ draft conversions, pure.
 *
 * A description and a thread item's `content[]` are the same thing: an ordered array of `text` and
 * `file` blocks. The composer edits that as ONE string with a `[name]` token where each file sits,
 * which is why both directions live here and why the token never reaches storage.
 */

// A block this app can render AND round-trip. A `file` part with no alias names bytes nobody can
// fetch, so it is dropped here rather than rendered and posted again.
function isBlock(part) {
  if ( !part || "string" !== typeof part.type ) return false;
  if ( "file" === part.type ) return !!(part.file && part.file.alias && part.file.name);
  return true;
}

/** Content as blocks: a string is one text block, nothing is none. */
export function normalizeContent(value) {
  if ( null === value || undefined === value ) return [];
  if ( "string" === typeof value ) return value ? [{ type: "text", text: value }] : [];
  if ( !Array.isArray(value) ) return [];
  return value.filter(isBlock);
}

/** The blocks a text field cannot show (`json`, anything unknown), in order — kept so an edit never drops them. */
export function opaqueBlocks(content) {
  return normalizeContent(content).filter((block) => "text" !== block.type && "file" !== block.type);
}

// generateUniqueId is crypto.randomUUID, absent in an insecure context — and this runs in the
// Composer's constructor and reseed(), where a throw takes the whole panel down. The composer's
// store() already treats the same call as a degradable failure (index.js); this is the same rule on
// the read side, with a per-tab counter standing in for the uuid.
let fallbacks = 0;
function chipId() {
  try {
    return Utils.generateUniqueId();
  } catch (e) {
    fallbacks += 1;
    return `chip-${fallbacks}`;
  }
}

/**
 * The composer's draft for this content: the text with a `[name]` token where each file sat, and one
 * chip per file. A chip for bytes already on the server carries no local File, so its hover preview
 * resolves through fileBlocks' cache instead of URL.createObjectURL.
 *
 * The chip's name is the stored name made token-safe by the SAME function the write path uses, so a
 * `[` or `]` in a name another producer wrote cannot become a token nothing matches. The stored block's
 * display name is then normalised on the next save — the alias, which is the storage key, is untouched.
 */
export function contentToDraft(content) {
  const pieces = [];
  const chips = [];
  normalizeContent(content).forEach((block) => {
    if ( "text" === block.type ) {
      if ( block.text ) pieces.push(block.text);
      return;
    }
    if ( "file" !== block.type ) return;
    const name = tokenSafeName(block.file.name);
    chips.push({
      id: chipId(),
      file: null,
      name,
      contentType: block.file.content_type || "",
      size: block.file.size,
      status: "uploaded",
      error: null,
      alias: block.file.alias
    });
    pieces.push(`[${name}]`);
  });
  return { text: pieces.join(" "), chips };
}

/** The prose: the join of the text blocks, one per line. Takes a string too. */
export function plainText(value) {
  return normalizeContent(value)
    .filter((block) => "text" === block.type && block.text)
    .map((block) => block.text)
    .join("\n");
}

/** Whether two content values mean the same thing — what a caller uses to skip a write. */
export function sameContent(a, b) {
  return JSON.stringify(normalizeContent(a)) === JSON.stringify(normalizeContent(b));
}

/** Whether there is anything to render. */
export function hasContent(value) {
  return normalizeContent(value).length > 0;
}
